import { useCallback, useEffect, useState } from 'react'
import { Download, Lock, Mail } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { useTranslation } from 'react-i18next'
import { MailExportDialog } from '@/components/mail/MailExportDialog'
import { apiErrorText } from '@/lib/api-error'
import { ausgabeAnfrage, fetchAusgaben, ladeAusgabe, type LvAusgabe } from '../api/calculation-lv.api'
import { datumText } from '../lib/kalkulation-labels'

const ART: Record<LvAusgabe['kind'], string> = { OFFER_FILE: 'Angebots-LV', PRINT: 'Druckbild', FORM_K3: 'Formblatt K3', FORM_K7: 'Formblatt K7' }

/**
 * AP-37 Detailblatt K 7.2 — **die Ausgaben aller Fassungen** dieser Kalkulation
 * (K-21), neueste oben: Datum, Fassung, Art, Format, Angebotspreis, Benutzer,
 * festgeschrieben; Download und E-Mail der gespeicherten Datei. Die Ausgaben der
 * geöffneten Fassung sind hervorgehoben, Beilagen stehen eingerückt darunter.
 */
export function LvAusgaben({ kalkulationId, nummer, stand }: { kalkulationId: string; nummer: string; stand: string }) {
  const { t } = useTranslation()
  const [liste, setListe] = useState<LvAusgabe[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [mail, setMail] = useState<LvAusgabe | null>(null)

  const laden = useCallback(async () => {
    try {
      setListe(await fetchAusgaben(kalkulationId))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Ausgaben ließen sich nicht laden.'))
    }
  }, [kalkulationId])
  useEffect(() => {
    void laden()
  }, [laden, stand])

  const herunterladen = async (a: LvAusgabe) => {
    try {
      await ladeAusgabe(kalkulationId, a)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Datei ließ sich nicht laden.'))
    }
  }

  const zeile = (a: LvAusgabe, beilage = false) => (
    <tr key={a.id} className={`border-t border-border/60 ${a.thisRevision ? '' : 'text-muted-foreground'}`}>
      <td className={`py-1 pr-2 ${beilage ? 'pl-5' : ''}`}>{datumText(a.createdAt, true)}</td>
      <td className="py-1 pr-2 tabular-nums">F{a.revision}</td>
      <td className="py-1 pr-2">{ART[a.kind]}</td>
      <td className="py-1 pr-2">{[a.format, a.formatVersion].filter(Boolean).join(' ')}</td>
      <td className="py-1 pr-2 text-right tabular-nums">{a.totals?.gross !== undefined ? geldText(a.totals.gross) : ''}</td>
      <td className="py-1 pr-2">{a.createdByName ?? ''}</td>
      <td className="py-1 pr-2" title={a.lockedRevision ? 'als abgegeben gekennzeichnet — die Fassung ist festgeschrieben' : undefined}>
        {a.lockedRevision && <Lock className="h-3.5 w-3.5" />}
      </td>
      <td className="py-1 text-right whitespace-nowrap">
        <button type="button" className="mr-2 text-primary hover:underline" title={`${a.fileName} · SHA-256 ${a.sha256}`} onClick={() => void herunterladen(a)}>
          <Download className="inline h-4 w-4" />
        </button>
        <button type="button" className="text-primary hover:underline" title="per E-Mail" onClick={() => setMail(a)}>
          <Mail className="inline h-4 w-4" />
        </button>
      </td>
    </tr>
  )

  if (!liste) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null
  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-1 text-sm font-semibold">Ausgaben</h3>
      {liste.length === 0 ? (
        <p className="text-xs text-muted-foreground">Noch nichts ausgegeben. Gespeichert wird nur, was ausgegeben wird — mit Prüfsumme als Nachweis.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="pb-1 pr-2 font-medium">am</th>
              <th className="pb-1 pr-2 font-medium">Fassung</th>
              <th className="pb-1 pr-2 font-medium">Art</th>
              <th className="pb-1 pr-2 font-medium">Format</th>
              <th className="pb-1 pr-2 text-right font-medium">Angebotspreis</th>
              <th className="pb-1 pr-2 font-medium">von</th>
              <th className="pb-1 pr-2 font-medium" />
              <th className="pb-1" />
            </tr>
          </thead>
          <tbody>{liste.flatMap((a) => [zeile(a), ...(a.attachments ?? []).map((b) => zeile(b, true))])}</tbody>
        </table>
      )}
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      {mail && (
        <MailExportDialog
          open
          onClose={() => setMail(null)}
          source="CALCULATION_LV_OUTPUT"
          title={t('mailExport.titles.CALCULATION_LV_OUTPUT')}
          defaultSubject={`${t('mailExport.titles.CALCULATION_LV_OUTPUT')} ${nummer} F${mail.revision}`}
          request={() => ausgabeAnfrage(kalkulationId, mail.id)}
          formats={['pdf']}
        />
      )}
    </section>
  )
}
